/**
 * Dashboard Page
 * Role-specific tile-based overview:
 * - Admin: Subjects, Sites, Users, Queries, Audit Trail (5 tiles)
 * - Site-RC/Site: My Subjects, Queries (2 tiles)
 * - Analyst: Subjects, Images & Documents, Queries (3 tiles)
 * - CEC: Adverse Events, Subjects, Queries (3 tiles)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queriesApi, cecApi, exportApi, filesApi, sourceDocsApi } from '../services/api';
import {
  MessageSquare,
  AlertTriangle,
  Shield,
  ClipboardCheck,
  Download,
  Loader2,
  Image,
  FileText,
  Plus,
  Pencil,
} from 'lucide-react';

// Helper to format file sizes
const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const role: string = user?.role || 'site-rc';

  // Fetch subjects
  const { data: subjectsData } = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => {
      const res = await fetch('/api/subjects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
  });

  // Fetch sites
  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const res = await fetch('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
  });

  // Fetch open queries
  const { data: queriesData } = useQuery({
    queryKey: ['queries', 'open'],
    queryFn: () => queriesApi.list({ status: 'open' }),
  });

  // Fetch all queries for resolved count
  const { data: allQueriesData } = useQuery({
    queryKey: ['queries', 'all'],
    queryFn: () => queriesApi.list({}),
  });

  // Fetch CEC summary (for CEC and admin)
  const { data: cecSummaryData } = useQuery({
    queryKey: ['cec', 'summary'],
    queryFn: () => cecApi.getSummary(),
    enabled: role === 'cec' || role === 'admin',
  });

  // Fetch users (admin only)
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    enabled: role === 'admin',
  });

  // Fetch file stats (admin + analyst)
  const { data: fileStatsData } = useQuery({
    queryKey: ['files', 'stats'],
    queryFn: () => filesApi.getStats(),
    enabled: role === 'admin' || role === 'analyst',
  });

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [downloadingSourceDocs, setDownloadingSourceDocs] = useState(false);

  const subjects = subjectsData?.subjects || [];
  const sites = sitesData?.sites || [];
  const openQueries = queriesData?.queries || [];
  const allQueries = allQueriesData?.queries || [];
  const resolvedQueries = allQueries.filter((q: any) => q.status === 'resolved');
  const cecSummary = cecSummaryData?.summary;
  const users = usersData?.users || [];

  // Filter subjects for site-rc based on assigned sites
  const mySubjects = (role === 'site-rc' || role === 'site') && user?.assignedSites
    ? subjects.filter((s: any) => {
        const siteId = typeof s.site === 'object' ? s.site._id : s.site;
        return user.assignedSites?.some(as => as._id === siteId);
      })
    : subjects;

  const handleDownloadFiles = async () => {
    if (!window.confirm(
      'This downloads images & documents for ALL subjects as one ZIP and may be very large and take a long time.\n\n' +
      'To download a single subject faster, open that patient and use the Download button (per group or per file) in the Images & Files section.\n\n' +
      'Continue with the full download?'
    )) return;
    try {
      setDownloadingAll(true);
      await filesApi.downloadAll();
    } catch (err: any) {
      console.error('Download error:', err);
      alert(err?.message === 'No files found' ? 'No files have been uploaded yet.' : (err?.message || 'Download failed'));
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSourceDocs = async () => {
    if (!window.confirm(
      'This downloads source documents for ALL subjects as one ZIP and may be very large and take a long time.\n\n' +
      'To download a single subject faster, open that patient and use the Download button (per group or per file) in the Images & Files section.\n\n' +
      'Continue with the full download?'
    )) return;
    try {
      setDownloadingSourceDocs(true);
      await sourceDocsApi.downloadAll();
    } catch (err: any) {
      console.error('Source docs download error:', err);
      alert(err?.message === 'No source documents found' ? 'No source documents have been uploaded yet.' : (err?.message || 'Download failed'));
    } finally {
      setDownloadingSourceDocs(false);
    }
  };

  const handleExportAll = async () => {
    try {
      setExportingAll(true);
      await exportApi.downloadAllCsv(user?.studyCode || 'AERO-CAD');
    } catch (error: any) {
      console.error('Export error:', error);
      alert(error?.message || 'Export failed');
    } finally {
      setExportingAll(false);
    }
  };

  // Role label
  const getRoleLabel = () => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'site-rc': return 'Site Research Coordinator';
      case 'site': return 'Site User';
      case 'analyst': return 'Core Lab Analyst';
      case 'cec': return 'Clinical Events Committee';
      default: return role;
    }
  };

  return (
    <div>
      {/* Header area - study name + role */}
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-gray-800">{user?.studyCode || 'Study'}</h1>
        <p className="text-xs text-gray-400">{getRoleLabel()}</p>
      </div>

      {/* ===================== ADMIN DASHBOARD ===================== */}
      {role === 'admin' && (
        <div className="space-y-5">
          {/* Top row: Subjects + Sites */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Tile: Subjects */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Subjects</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{subjects.length}</p>
                </div>
                <Link to="/subjects" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              {/* Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                <Link to="/data" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <Pencil className="w-4 h-4 text-gray-400" />
                  Data Entry
                </Link>
                <button
                  onClick={handleExportAll}
                  disabled={exportingAll}
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1 disabled:opacity-50"
                >
                  {exportingAll ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : <Download className="w-4 h-4 text-gray-400" />}
                  Download Data (CSV)
                </button>
                <button
                  onClick={handleDownloadFiles}
                  disabled={downloadingAll}
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1 disabled:opacity-50"
                >
                  {downloadingAll ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : <Image className="w-4 h-4 text-gray-400" />}
                  Download Images & Documents (ZIP)
                </button>
                <button
                  onClick={handleDownloadSourceDocs}
                  disabled={downloadingSourceDocs}
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1 disabled:opacity-50"
                >
                  {downloadingSourceDocs ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : <FileText className="w-4 h-4 text-gray-400" />}
                  Download Source Documents (ZIP)
                </button>
              </div>
            </div>

            {/* Tile: Queries */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Queries</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{openQueries.length + resolvedQueries.length}</p>
                </div>
                <Link to="/queries" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-400">Open</span>
                    <span className={`ml-2 font-medium ${openQueries.length > 0 ? 'text-amber-600' : 'text-gray-700'}`}>{openQueries.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Resolved</span>
                    <span className="ml-2 font-medium text-gray-700">{resolvedQueries.length}</span>
                  </div>
                </div>
              </div>
              {openQueries.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  {openQueries.slice(0, 3).map((q: any) => (
                    <div key={q._id} className="text-sm">
                      <p className="text-gray-700 truncate">{q.queryText}</p>
                      <p className="text-xs text-gray-400">{q.subjectId} — {q.formId}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-400">
                  No open queries at this time.
                </div>
              )}
            </div>
          </div>

          {/* Bottom row: Sites + Users + Audit Trail */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Tile: Sites */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Sites</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{sites.length}</p>
                </div>
                <Link to="/admin/sites" className="text-xs text-opci-gold mt-1">Manage →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-400">Active</span>
                    <span className="ml-2 font-medium text-gray-700">{sites.filter((s: any) => s.isActive !== false).length}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Subjects</span>
                    <span className="ml-2 font-medium text-gray-700">{subjects.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tile: Users */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{users.filter((u: any) => u.isActive).length}</p>
                </div>
                <Link to="/admin/users" className="text-xs text-opci-gold mt-1">Manage →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-4 text-sm flex-wrap">
                  {['admin', 'site-rc', 'analyst', 'cec'].map(r => {
                    const count = users.filter((u: any) => u.role === r && u.isActive).length;
                    if (count === 0) return null;
                    return (
                      <div key={r}>
                        <span className="text-gray-400">{r === 'site-rc' ? 'Site RC' : r === 'cec' ? 'CEC' : r.charAt(0).toUpperCase() + r.slice(1)}</span>
                        <span className="ml-1.5 font-medium text-gray-700">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Tile: Audit Trail */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Audit Trail</p>
                </div>
                <Link to="/audit" className="text-xs text-opci-gold mt-1">View Full Log →</Link>
              </div>
              <div className="mt-4 text-sm text-gray-400">
                View the complete audit trail for all system activity, data changes, and user actions.
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <Link
                  to="/audit"
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1"
                >
                  <Shield className="w-4 h-4 text-gray-400" />
                  Open Audit Trail
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== SITE-RC / SITE DASHBOARD ===================== */}
      {(role === 'site-rc' || role === 'site') && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Tile: My Subjects */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">My Subjects</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{mySubjects.length}</p>
                </div>
                <Link to="/subjects" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              {/* Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                <Link to="/subjects?action=new" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <Plus className="w-4 h-4 text-gray-400" />
                  Add New Subject
                </Link>
                <Link to="/data" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <Pencil className="w-4 h-4 text-gray-400" />
                  Enter Data
                </Link>
              </div>
            </div>

            {/* Tile: Queries */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Queries</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{openQueries.length}</p>
                </div>
                <Link to="/queries" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-400">To Answer</span>
                    <span className={`ml-2 font-medium ${openQueries.length > 0 ? 'text-amber-600' : 'text-gray-700'}`}>{openQueries.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Resolved</span>
                    <span className="ml-2 font-medium text-gray-700">{resolvedQueries.length}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <Link to="/queries" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  Answer Queries
                </Link>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ===================== ANALYST DASHBOARD ===================== */}
      {role === 'analyst' && (
        <div className="space-y-5">
          {/* Top row: Subjects + Images & Documents */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Tile: Subjects */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Subjects</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{subjects.length}</p>
                </div>
                <Link to="/subjects" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              {/* Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                <Link to="/data" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <Pencil className="w-4 h-4 text-gray-400" />
                  Enter Analysis
                </Link>
              </div>
            </div>

            {/* Tile: Images & Documents */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Images & Documents</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{fileStatsData?.totalFiles || 0}</p>
                </div>
                {fileStatsData && fileStatsData.totalFiles > 0 && (
                  <button
                    onClick={handleDownloadFiles}
                    disabled={downloadingAll}
                    className="text-xs text-opci-gold mt-1 hover:text-opci-gold-600 disabled:opacity-50"
                  >
                    {downloadingAll ? 'Downloading...' : 'Download All →'}
                  </button>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-400">Subjects</span>
                    <span className="ml-2 font-medium text-gray-700">
                      {fileStatsData?.subjectsWithFiles || 0} of {fileStatsData?.totalSubjects || subjects.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Storage</span>
                    <span className="ml-2 font-medium text-gray-700">
                      {fileStatsData ? formatSize(fileStatsData.totalSize) : '0 KB'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-400">
                Angiography, OCT, IVUS images and procedure documents uploaded by sites.
              </div>
            </div>

          </div>

          {/* Bottom: Queries (full width) */}
          <div className="bg-white rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Queries</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{openQueries.length}</p>
              </div>
              <Link to="/queries" className="text-xs text-opci-gold mt-1">View All →</Link>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-gray-400">Open</span>
                  <span className="ml-2 font-medium text-gray-700">{openQueries.length}</span>
                </div>
                <div>
                  <span className="text-gray-400">Resolved</span>
                  <span className="ml-2 font-medium text-gray-700">{resolvedQueries.length}</span>
                </div>
              </div>
            </div>
            {openQueries.length === 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-400">
                No open queries at this time.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== CEC DASHBOARD ===================== */}
      {role === 'cec' && (
        <div className="space-y-5">
          {/* Top row: Adverse Events + Subjects */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Tile: Adverse Events */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Adverse Events</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{cecSummary?.totalAEs || 0}</p>
                </div>
                <Link to="/cec" className="text-xs text-opci-gold mt-1">CEC Dashboard →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-gray-400">Pending</span>
                    <span className={`ml-2 font-medium ${(cecSummary?.pendingAEs || 0) > 0 ? 'text-amber-600' : 'text-gray-700'}`}>
                      {cecSummary?.pendingAEs || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Adjudicated</span>
                    <span className="ml-2 font-medium text-gray-700">{cecSummary?.adjudicatedAEs || 0}</span>
                  </div>
                </div>
              </div>
              {/* Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                <Link to="/cec?status=pending" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <ClipboardCheck className="w-4 h-4 text-gray-400" />
                  Start Adjudicating
                </Link>
                <Link to="/cec" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  Review All Events
                </Link>
                <button
                  onClick={handleDownloadSourceDocs}
                  disabled={downloadingSourceDocs}
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-opci-gold-600 py-1 disabled:opacity-50"
                >
                  {downloadingSourceDocs ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : <FileText className="w-4 h-4 text-gray-400" />}
                  Download Source Documents (ZIP)
                </button>
              </div>
            </div>

            {/* Tile: Subjects */}
            <div className="bg-white rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Subjects</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{subjects.length}</p>
                </div>
                <Link to="/subjects" className="text-xs text-opci-gold mt-1">View All →</Link>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 text-sm text-gray-400">
                Subject data is read-only. Use the CEC Dashboard to review and adjudicate events.
              </div>
            </div>

          </div>

          {/* Bottom: Queries (full width) */}
          <div className="bg-white rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Queries</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{openQueries.length}</p>
              </div>
              <Link to="/queries" className="text-xs text-opci-gold mt-1">View All →</Link>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-gray-400">Open</span>
                  <span className="ml-2 font-medium text-gray-700">{openQueries.length}</span>
                </div>
                <div>
                  <span className="text-gray-400">Resolved</span>
                  <span className="ml-2 font-medium text-gray-700">{resolvedQueries.length}</span>
                </div>
              </div>
            </div>
            {openQueries.length === 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-400">
                No open queries at this time.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
